import '../../assets/css/case-property.css';
// @ts-ignore
import { useProperty, useContentToken, EditableToken, RichTextContent, useSlotGroup, EditableSlotGroup, useCollectionBinding } from '@vibesites/runtime/react';
// @ts-ignore
import type { ComponentContentSchema, SlotGroupDefinition, CollectionBindingDefinition } from '@vibesites/runtime';
import type { Amenity, Floorplan } from '@vibesites/schema';
import { getPropertyBasePath, formatPhone, phoneDigits } from './propertyUtils';

// ── Slot Group Definitions ───────────────────────────────────────────────

const SLOT_DEFS: Record<string, SlotGroupDefinition> = {
  'propertyresourcesfooter.amenities_cta': {
    label: 'Amenities Card CTA',
    description: 'Title and link for the amenities resource card.',
    fields: {
      title: { label: 'Card Title', type: 'text', defaultField: 'property_name', defaultValue: 'Amenities' },
      cta_text: { label: 'CTA Text', type: 'text', defaultValue: 'View All Amenities' },
      cta_url: { label: 'CTA URL', type: 'url', defaultValue: '/amenities' },
      _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
    },
  },
  'propertyresourcesfooter.floorplans_cta': {
    label: 'Floor Plans Card CTA',
    description: 'Link for the floor plans resource card.',
    fields: {
      cta_text: { label: 'CTA Text', type: 'text', defaultValue: 'View Floor Plans' },
      cta_url: { label: 'CTA URL', type: 'url', defaultValue: '/floor-plans' },
      _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
    },
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────

interface FloorplanGroup {
  beds: string;
  baths: string;
  sqftMin: number | null;
  sqftMax: number | null;
}

function validNum(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Deduplicate floorplans by bed/bath combo, aggregate sqft ranges,
 * and skip entries with missing/invalid bed or bath values.
 */
function buildFloorplanSummaries(plans: Floorplan[]): string[] {
  const groups = new Map<string, FloorplanGroup>();

  for (const fp of plans) {
    const fpExt = fp as Floorplan & { beds?: string; baths?: string; sqft?: string };
    const beds = String(fp.floorplan_beds_min ?? fpExt.beds ?? '').trim();
    const baths = String(fp.floorplan_baths_min ?? fpExt.baths ?? '').trim();
    if (!beds || !baths) continue;
    if (!Number.isFinite(Number(beds)) || !Number.isFinite(Number(baths))) continue;

    const key = `${beds}|${baths}`;
    const sqftMin = validNum(fp.floorplan_sqft_min ?? fpExt.sqft);
    const sqftMax = validNum(fp.floorplan_sqft_max);

    const existing = groups.get(key);
    if (existing) {
      if (sqftMin !== null) {
        existing.sqftMin = existing.sqftMin !== null ? Math.min(existing.sqftMin, sqftMin) : sqftMin;
      }
      const upper = sqftMax ?? sqftMin;
      if (upper !== null) {
        existing.sqftMax = existing.sqftMax !== null ? Math.max(existing.sqftMax, upper) : upper;
      }
    } else {
      groups.set(key, { beds, baths, sqftMin, sqftMax: sqftMax ?? sqftMin });
    }
  }

  // Sort by bedrooms ascending, then baths ascending
  const sorted = Array.from(groups.values()).sort((a, b) => {
    const bedDiff = Number(a.beds) - Number(b.beds);
    if (bedDiff !== 0) return bedDiff;
    return Number(a.baths) - Number(b.baths);
  });

  const results: string[] = [];
  for (const g of sorted) {
    const bedBath = `${g.beds}bed/${g.baths}bath`;
    if (g.sqftMin !== null && g.sqftMax !== null && g.sqftMin !== g.sqftMax) {
      results.push(`${bedBath} ${g.sqftMin.toLocaleString()} – ${g.sqftMax.toLocaleString()} Sq. Ft.`);
    } else if (g.sqftMin !== null) {
      results.push(`${bedBath} ${g.sqftMin.toLocaleString()} Sq. Ft.`);
    } else {
      results.push(bedBath);
    }
  }
  return results;
}

// ── Component ────────────────────────────────────────────────────────────

/**
 * "Have a question?" footer with phone/contact bar and two resource link cards
 * (amenities + floor plans). Renders real property data via collection bindings
 * and lets editors customise titles/CTAs through slot groups.
 */
export function PropertyResourcesFooter() {
  const property = useProperty();
  const name = property.property_name || '';
  const phone = property.property_call_tracking_phone || property.property_phone || '';
  const basePath = getPropertyBasePath(property);

  // Content tokens
  const heading = useContentToken('propertyresourcesfooter.heading');
  const contactText = useContentToken('propertyresourcesfooter.contact_text');

  // Slot groups — card titles + CTA links
  const amenitiesCta = useSlotGroup(
    'propertyresourcesfooter.amenities_cta',
    SLOT_DEFS['propertyresourcesfooter.amenities_cta'],
  );
  const floorplansCta = useSlotGroup(
    'propertyresourcesfooter.floorplans_cta',
    SLOT_DEFS['propertyresourcesfooter.floorplans_cta'],
  );

  // Collection bindings
  const amenities = useCollectionBinding<Amenity>('propertyresourcesfooter.amenities_list', {
    collection: 'amenities',
    defaultLimit: contentSchema.collectionBindings!['propertyresourcesfooter.amenities_list'].defaultLimit,
  });
  const floorplans = useCollectionBinding<Floorplan>('propertyresourcesfooter.floorplans_list', {
    collection: 'floorplans',
  });

  // Derive display values
  const amenityTitle = amenitiesCta.title ? `Amenities at ${amenitiesCta.title}` : `Amenities at ${name}`;
  const amenityCtaText = amenitiesCta.cta_text || 'View All Amenities';
  const amenityCtaUrl = amenitiesCta.cta_url && amenitiesCta.cta_url !== '/amenities'
    ? amenitiesCta.cta_url
    : `${basePath}/amenities`;
  const amenityTarget = amenitiesCta._target || '_self';

  const fpCtaText = floorplansCta.cta_text || 'View Floor Plans';
  const fpCtaUrl = floorplansCta.cta_url && floorplansCta.cta_url !== '/floor-plans'
    ? floorplansCta.cta_url
    : `${basePath}/floor-plans`;
  const fpTarget = floorplansCta._target || '_self';

  const amenityItems = amenities.map((a) => a.title).filter(Boolean);
  const floorplanItems = buildFloorplanSummaries(floorplans);

  return (
    <section className="prop-al-resources">
      <div className="prop-al-container-main">
        {phone && (
          <div className="prop-al-resource-contact">
            <EditableToken path="propertyresourcesfooter.heading" type="text" label="Heading" as="h3" style={{ fontSize: '1.25rem', fontWeight: 700 }} defaultValue={contentSchema.tokens['propertyresourcesfooter.heading'].defaultValue}>
              {heading || 'Have a specific question? Give us a call at'}
            </EditableToken>
            <h3>
              <a href={`tel:${phoneDigits(phone)}`}>{formatPhone(phone)}</a>
              {' or '}
              <a href={`${basePath}/contact-us`}>
                <EditableToken path="propertyresourcesfooter.contact_text" type="text" label="Contact Link Text" as="span" defaultValue={contentSchema.tokens['propertyresourcesfooter.contact_text'].defaultValue}>
                  {contactText || 'Contact Us'}
                </EditableToken>
              </a>
            </h3>
          </div>
        )}

        <div className="prop-al-resource-grid">
          {/* Amenities card */}
          <div className="prop-al-resource-card">
            <EditableSlotGroup groupPath="propertyresourcesfooter.amenities_cta" definition={SLOT_DEFS['propertyresourcesfooter.amenities_cta']}>
              <h3>{amenityTitle}</h3>
            </EditableSlotGroup>
            {amenityItems.length > 0 && (
              <ul>
                {amenityItems.map((item, j) => (
                  <li key={j}>{item}</li>
                ))}
              </ul>
            )}
            <EditableSlotGroup groupPath="propertyresourcesfooter.amenities_cta" definition={SLOT_DEFS['propertyresourcesfooter.amenities_cta']}>
              <a className="prop-al-resource-link" href={amenityCtaUrl} target={amenityTarget}>{amenityCtaText}</a>
            </EditableSlotGroup>
          </div>

          {/* Floor Plans card */}
          <div className="prop-al-resource-card">
            <RichTextContent
              path="propertyresourcesfooter.floorplans_title"
              label="Floor Plans Card Title"
              defaultValue={contentSchema.tokens['propertyresourcesfooter.floorplans_title'].defaultValue}
              as="h3"
            />
            {floorplanItems.length > 0 && (
              <ul>
                {floorplanItems.map((item, j) => (
                  <li key={j}>{item}</li>
                ))}
              </ul>
            )}
            <EditableSlotGroup groupPath="propertyresourcesfooter.floorplans_cta" definition={SLOT_DEFS['propertyresourcesfooter.floorplans_cta']}>
              <a className="prop-al-resource-link" href={fpCtaUrl} target={fpTarget}>{fpCtaText}</a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Content Schema ───────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyResourcesFooter',
  displayName: 'Property Resources Footer',
  tokens: {
    'propertyresourcesfooter.heading': {
      label: 'Contact Heading',
      type: 'text',
      defaultValue: 'Have a specific question? Give us a call at',
    },
    'propertyresourcesfooter.contact_text': {
      label: 'Contact Link Text',
      type: 'text',
      defaultValue: 'Contact Us',
    },
    'propertyresourcesfooter.floorplans_title': {
      label: 'Floor Plans Card Title',
      type: 'richtext',
      defaultValue: 'Apartments at <span data-type="field-token" data-field="property_name"></span>',
    },
  },
  slotGroups: SLOT_DEFS,
  collectionBindings: {
    'propertyresourcesfooter.amenities_list': {
      label: 'Amenities List',
      collection: 'amenities',
      description: 'Amenity items shown in the resource card.',
      defaultLimit: 5,
    } as CollectionBindingDefinition,
    'propertyresourcesfooter.floorplans_list': {
      label: 'Floor Plans List',
      collection: 'floorplans',
      description: 'Floor plan items shown in the resource card.',
    } as CollectionBindingDefinition,
  },
};

(PropertyResourcesFooter as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyResourcesFooter;
